// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  FleetFireExtinguisherRow,
  FleetFireExtinguisherSearchFormValues,
  FleetFireExtinguisherSummary,
} from "services/api/fleet/vehicles/fireextinguishers/types";
import {
  getFleetFireExtinguishersAPI,
  getFleetFireExtinguisherSummaryAPI,
} from "services/api/fleet/vehicles/fireextinguishers/api";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";

// Tipovi i konstante
import {
  FireExtinguisherSearchPageCmp,
  FireExtinguisherSearchPageProps,
} from "./types";
import { FLEET_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes } from "ui/modules/fleet/fireExtinguishers/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalne komponente
import FireExtinguisherStatusTabs from "./components/FireExtinguisherStatusTabs/FireExtinguisherStatusTabs";
import FireExtinguisherSearchFilters from "./components/FireExtinguisherSearchFilters/FireExtinguisherSearchFilters";
import FireExtinguisherSearchResults from "./components/FireExtinguisherSearchResults/FireExtinguisherSearchResults";

const initialFilters: FleetFireExtinguisherSearchFormValues = {
  search: "",
  expirationDateFrom: null,
  expirationDateTo: null,
  status: "",
  vehicleManufacturerId: null,
  vehicleModelId: null,
  vehicleTypeId: null,
};

const initialSummary: FleetFireExtinguisherSummary = {
  expired: 0,
  expiring: 0,
  valid: 0,
  total: 0,
};

const FireExtinguisherSearchPage: FireExtinguisherSearchPageCmp = (
  _props: FireExtinguisherSearchPageProps
) => {
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetFireExtinguisherRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  // Datumski raspon sinkroniziran s URL-om (drill-down s dashboarda + share/reload).
  const [filterValues, setFilterValues] = useUrlFilters<FleetFireExtinguisherSearchFormValues>(
    initialFilters,
    {
      expirationDateFrom: { param: "expirationDateFrom", type: "date" },
      expirationDateTo: { param: "expirationDateTo", type: "date-end" },
    }
  );
  const [summary, setSummary] =
    useState<FleetFireExtinguisherSummary>(initialSummary);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetFireExtinguisherSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(
        await getFleetFireExtinguishersAPI(
          pagination,
          values.search,
          values.expirationDateFrom,
          values.expirationDateTo,
          values.status,
          values.vehicleManufacturerId,
          values.vehicleModelId,
          values.vehicleTypeId
        )
      );
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchSummary = async () => {
    try {
      setSummary(await getFleetFireExtinguisherSummaryAPI());
    } catch {
      setSummary(initialSummary);
    }
  };

  useEffect(() => {
    // Kreni s vrijednostima iz URL-a (useUrlFilters ih je već popunio).
    fetchSearchResult(searchResults.pagination, filterValues);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (values: FleetFireExtinguisherSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      const values = { ...filterValues, status };
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues, searchResults.pagination]
  );

  const handlePaginationChange = (pagination: Pagination) => {
    fetchSearchResult(pagination, filterValues);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  const title = FLEET_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={title.text}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <FireExtinguisherStatusTabs
            activeStatus={filterValues.status}
            summary={summary}
            onStatusChange={handleStatusChange}
          />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <FireExtinguisherSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema vatrogasnih aparata" />
          ) : (
            <FireExtinguisherSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

FireExtinguisherSearchPage.route = Routes.FireExtinguisherSearchPageRouter;

export default FireExtinguisherSearchPage;
